import { useEffect, useRef, useState } from "react";

/**
 * Measure an element's content-box width with a ResizeObserver.
 *
 * Vega-Lite's native `width: "container"` is unreliable for our layered charts
 * (see components/charts/vega-specs.ts), so instead we measure the wrapper element and feed
 * a concrete pixel width into the chart spec. This makes charts fill whatever
 * column or full-width container they live in, in both the 2-column and
 * standalone (full-width) flow layouts.
 *
 * Returns a `[ref, width]` tuple; `width` is `null` until the first measurement.
 */
export function useContainerWidth<T extends HTMLElement = HTMLDivElement>(): [
  React.RefObject<T | null>,
  number | null,
] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) {
          setWidth((prev) => (prev === Math.round(w) ? prev : Math.round(w)));
        }
      }
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return [ref, width];
}
